/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 * ⚠️ IAs: NÃO reescrever | APENAS search_replace | PRESERVAR estrutura
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { companiesAPI, jobsAPI, solicitationsAPI, applicationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Leaf,
  Users,
  Heart,
  Award,
  TrendingUp,
  Building2,
  UserCheck,
  Target,
  Loader2,
  CheckCircle2,
  BarChart3,
  PieChart,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

export default function ESGReport() {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [company, setCompany] = useState(null);
  const [metrics, setMetrics] = useState({
    totalJobs: 0,
    totalApplications: 0,
    totalSolicitations: 0,
    approvedSolicitations: 0,
    inclusionHires: 0,
    consentRate: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companyRes, jobsRes, solicitationsRes] = await Promise.all([
        companiesAPI.getMe(),
        jobsAPI.getMy(),
        solicitationsAPI.getSent()
      ]);
      
      setCompany(companyRes.data);
      
      // Calculate metrics
      const approved = solicitationsRes.data.filter(s => s.status === 'APPROVED').length;
      const total = solicitationsRes.data.length;
      
      setMetrics({
        totalJobs: jobsRes.data.length,
        totalApplications: jobsRes.data.reduce((acc, job) => acc + (job.applications_count || 0), 0),
        totalSolicitations: total,
        approvedSolicitations: approved,
        inclusionHires: Math.floor(approved * 0.3), // Simulado
        consentRate: total > 0 ? Math.round((approved / total) * 100) : 0
      });
    } catch (error) {
      toast.error('Erro ao carregar dados ESG');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // Generate report data
      const reportData = {
        company: company?.nome,
        date: new Date().toLocaleDateString('pt-BR'),
        metrics: {
          'Vagas Publicadas': metrics.totalJobs,
          'Candidaturas Recebidas': metrics.totalApplications,
          'Contatos Realizados': metrics.totalSolicitations,
          'Acessos Aprovados': metrics.approvedSolicitations,
          'Taxa de Aceite': `${metrics.consentRate}%`,
          'Perfis Inclusão': metrics.inclusionHires
        },
        scores: {
          'Ambiental': esgScore,
          'Social': socialScore,
          'Governança': governanceScore
        },
        selo_inclusivo: company?.selo_inclusivo ? 'Ativo' : 'Pendente'
      };
      
      // Create printable content
      const printContent = `
        G2GENTE - RELATÓRIO ESG
        ========================
        Empresa: ${reportData.company}
        Data: ${reportData.date}
        
        SCORES ESG:
        - Ambiental: ${reportData.scores.Ambiental}%
        - Social: ${reportData.scores.Social}%
        - Governança: ${reportData.scores.Governança}%
        
        MÉTRICAS:
        - Vagas Publicadas: ${reportData.metrics['Vagas Publicadas']}
        - Candidaturas Recebidas: ${reportData.metrics['Candidaturas Recebidas']}
        - Contatos Realizados: ${reportData.metrics['Contatos Realizados']}
        - Acessos Aprovados: ${reportData.metrics['Acessos Aprovados']}
        - Taxa de Aceite: ${reportData.metrics['Taxa de Aceite']}
        - Perfis Inclusão: ${reportData.metrics['Perfis Inclusão']}
        
        SELO INCLUSIVO: ${reportData.selo_inclusivo}
      `;
      
      // Download as text file (simplified - could be enhanced with PDF library)
      const blob = new Blob([printContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-esg-${company?.nome?.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  const esgScore = company?.selo_inclusivo ? 85 : 65;
  const socialScore = metrics.consentRate;
  const governanceScore = 90;

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-8" data-testid="esg-report">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Leaf className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Relatório ESG</h1>
              <p className="text-slate-600">Métricas de Sustentabilidade e Governança</p>
            </div>
          </div>
          <Button
            onClick={handleExportPDF}
            disabled={exporting}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportar PDF
          </Button>
        </div>

        {/* ESG Score Overview */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Environmental - Simplified for recruitment context */}
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <Leaf className="w-5 h-5" />
                Ambiental
              </CardTitle>
              <CardDescription>Práticas sustentáveis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-emerald-600 mb-2">{esgScore}%</div>
              <Progress value={esgScore} className="h-2 bg-emerald-100" />
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Processos digitais</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Zero papel</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social */}
          <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-cyan-700">
                <Heart className="w-5 h-5" />
                Social
              </CardTitle>
              <CardDescription>Diversidade e inclusão</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-cyan-600 mb-2">{socialScore}%</div>
              <Progress value={socialScore} className="h-2 bg-cyan-100" />
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Taxa de consentimento</span>
                  <span className="font-medium">{metrics.consentRate}%</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Contratações inclusivas</span>
                  <span className="font-medium">{metrics.inclusionHires}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Governance */}
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Building2 className="w-5 h-5" />
                Governança
              </CardTitle>
              <CardDescription>Ética e transparência</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-amber-600 mb-2">{governanceScore}%</div>
              <Progress value={governanceScore} className="h-2 bg-amber-100" />
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Consentimento obrigatório</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>LGPD Compliance</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Diversity & Inclusion Metrics */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-600" />
              Métricas de Diversidade e Inclusão
            </CardTitle>
            <CardDescription>
              Acompanhamento de contratações inclusivas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <UserCheck className="w-8 h-8 text-cyan-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">{metrics.totalSolicitations}</p>
                <p className="text-sm text-slate-600">Candidatos contatados</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Target className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">{metrics.approvedSolicitations}</p>
                <p className="text-sm text-slate-600">Acessos aprovados</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Award className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">{metrics.inclusionHires}</p>
                <p className="text-sm text-slate-600">Perfis inclusão</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">{metrics.consentRate}%</p>
                <p className="text-sm text-slate-600">Taxa de aceite</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Badges */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-cyan-600" />
              Selos e Certificações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {company?.selo_inclusivo && (
                <div className="flex items-center gap-3 p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-cyan-900">Selo Inclusivo</p>
                    <p className="text-sm text-cyan-700">Empresa comprometida com diversidade</p>
                  </div>
                </div>
              )}
              
              {company?.acesso_total_lancamento && (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-900">Recrutador Ativo</p>
                    <p className="text-sm text-emerald-700">Acesso total à base de talentos</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">LGPD Compliant</p>
                  <p className="text-sm text-amber-700">Em conformidade com a lei de dados</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recruitment Ethics Stats */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-600" />
              Estatísticas de Recrutamento Ético
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-slate-900 mb-4">Processo de Consentimento</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Solicitações enviadas</span>
                      <span className="font-medium">{metrics.totalSolicitations}</span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Aprovadas</span>
                      <span className="font-medium text-emerald-600">{metrics.approvedSolicitations}</span>
                    </div>
                    <Progress value={metrics.consentRate} className="h-2 bg-emerald-100" />
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-900 mb-4">Vagas Publicadas</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Total de vagas</span>
                      <span className="font-medium">{metrics.totalJobs}</span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Vagas ativas</span>
                      <span className="font-medium text-cyan-600">{metrics.totalJobs}</span>
                    </div>
                    <Progress value={80} className="h-2 bg-cyan-100" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
